import { useEffect, useRef } from 'preact/hooks';
import type { Track } from 'livekit-client';
import { avatarStyle, initialsFor } from '../lib/identity.js';
import type { StagePerson } from '../lib/useRoom.js';

/**
 * A person with live video.
 *
 * The tile's SHAPE is not decided here, and deliberately not in JavaScript at
 * all: the <video> carries its own intrinsic aspect ratio, so the browser sizes
 * the tile from the frame the device actually sent. A phone held upright gets a
 * portrait tile, a laptop gets a landscape one, and a mid-show rotation is
 * followed for free.
 *
 * An earlier version measured videoWidth/videoHeight and fed a --tile-ratio
 * custom property to `aspect-ratio`. That is a worse design twice over: it can
 * disagree with the real frame, and a single bad value makes the whole
 * declaration invalid at computed-value time, which CSS resolves to
 * `aspect-ratio: auto` — collapsing the tile to the width of its borders. There
 * is no measurement left to be wrong.
 *
 * Attaching still happens HERE rather than centrally, because only the
 * component holding the element can render it; attaching in the hook would mean
 * an off-screen <video> per participant with a live decoder behind it.
 */
export function VideoTile({ person }: { person: StagePerson }) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const element = ref.current;
    const track = person.videoTrack as Track | null;
    if (!element || !track) return;
    track.attach(element);
    // Detach on unmount or track change — an orphan keeps a decoder alive.
    return () => {
      track.detach(element);
    };
  }, [person.videoTrack]);

  const speaking = person.isSpeaking;
  const muted = !person.hasMic || person.isMuted;

  return (
    <figure
      class={[
        'vtile',
        `vtile--${person.videoSource ?? 'camera'}`,
        speaking ? 'vtile--speaking' : '',
        person.isLocal ? 'vtile--self' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={avatarStyle(person.identity)}
    >
      <video
        ref={ref}
        class="vtile__video"
        autoplay
        playsinline
        /* Always muted: the AUDIO comes through its own element, and an unmuted
           video element would double every voice and re-trigger the autoplay
           block this product already fought once. */
        muted
      />
      <figcaption class="vtile__bar">
        <span class="vtile__name" title={person.name}>
          {person.name}
          {person.isLocal ? ' · you' : ''}
        </span>
        {muted ? <span class="vtile__muted" aria-label="Muted">🔇</span> : null}
        {person.videoSource === 'screen' ? <span class="vtile__tag">Screen</span> : null}
      </figcaption>
      {/* Shown until the first frame paints, so the tile is never a black box. */}
      <span class="vtile__placeholder" aria-hidden="true">
        {initialsFor(person.name || person.identity)}
      </span>
    </figure>
  );
}
